import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { PlusIcon } from '@heroicons/react/24/outline';
import LeftPanel from './LeftPanel';
import WorkspaceArea from './WorkspaceArea';
import MobileFieldsModal from './MobileFieldsModal';
import { FormField, StandardField, UserAttribute, TbitIntegration } from './types';
import { GET_FORM } from '~/services/forms';

const FormCreator = () => {
  const { t } = useTranslation('forms');
  const { agentId, formId } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(formId);
  
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [aiCompleted, setAiCompleted] = useState(true); // Por defecto true
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    standardFields: true,
    contactInfo: true,
    tbitIntegrations: true
  });

  // Query para obtener el formulario en modo edición
  const { data: formData, loading: formLoading, error: formError } = useQuery(GET_FORM, {
    variables: {
      data: {
        form_id: formId
      }
    },
    skip: !isEditMode || !formId,
    onCompleted: (data) => {
      if (data?.get_form?.data && !data.get_form.hasError) {
        loadFormData(data.get_form.data);
      }
    },
    onError: (error) => {
      console.error('Error loading form:', error);
      alert('Error al cargar el formulario para edición');
    }
  });

  // Función para cargar los datos del formulario en el estado
  const loadFormData = (form: any) => {
    setIsLoading(true);
    try {
      setFormName(form.name || '');
      setFormDescription(form.description || '');
      setAiCompleted(form.aiCompleted ?? true); // Usar aiCompleted directamente (sin guión bajo)
      
      // Transformar los campos del backend al formato del frontend
      const transformedFields: FormField[] = form.fields?.map((field: any, index: number) => {
        return {
          id: field.id || `field-${Date.now()}-${index}`,
          type: field.type?.toLowerCase() || 'standard',
          name: field.name || '',
          label: field.label || field.name || '',
          inputType: field.inputType?.toLowerCase() || 'text',
          dataType: field.dataType?.toLowerCase(),
          attributeId: field.attributeId,
          required: field.required ?? true,
          requestFromUser: field.requestFromUser ?? true,
          placeholder: field.placeholder || '',
          description: field.description || '',
          useAI: field.useAI ?? false,
          options: field.options || field.closelist || [],
          closelist: field.closelist || [],
          multiple: field.multiple ?? false,
          updatable: field.updatable ?? false,
          // Configuración API si existe
          apiConfig: field.apiConfig ? {
            url: field.apiConfig.url || '',
            method: field.apiConfig.method || 'GET',
            authType: field.apiConfig.authType || 'none',
            authToken: field.apiConfig.authToken || '',
            headers: field.apiConfig.headers || '',
            body: field.apiConfig.body || '',
            format: field.apiConfig.format || 'json',
            executeOnSubmit: field.apiConfig.executeOnSubmit ?? true,
            showResponse: field.apiConfig.showResponse ?? false,
            continueOnError: field.apiConfig.continueOnError ?? false
          } : undefined
        };
      }) || [];
      
      setFormFields(transformedFields);
    } catch (error) {
      console.error('Error transforming form data:', error);
      alert('Error al procesar los datos del formulario');
    } finally {
      setIsLoading(false);
    }
  };

  // Mutación para crear formulario - movida a WorkspaceArea

  const toggleSection = (section: 'standardFields' | 'contactInfo' | 'tbitIntegrations') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFieldClick = (field: StandardField | UserAttribute | TbitIntegration) => {
    let newField: FormField;
    
    if (field.type === 'standard') {
      newField = {
        id: `field-${Date.now()}`,
        type: 'standard',
        name: field.name,
        label: field.name,
        inputType: field.inputType,
        required: true, // Por defecto true
        requestFromUser: true,
        placeholder: `Ingresa ${field.name.toLowerCase()}`
      };

      // Para campos de lista, agregar el dataType apropiado
      if (field.inputType === 'closelist') {
        newField.dataType = 'closelist';
        newField.inputType = 'select';
      } else if (field.inputType === 'openlist') {
        newField.dataType = 'openlist';
        newField.inputType = 'select';
      }
    } else if (field.type === 'integration') {
      // Es una integración T-BIT
      newField = {
        id: `field-${Date.now()}`,
        type: 'integration',
        name: field.name,
        label: field.name,
        inputType: field.inputType,
        required: true, // Por defecto true
        requestFromUser: true,
        placeholder: `Configura ${field.name.toLowerCase()}`,
        // Inicializar configuración de API vacía
        apiConfig: {
          url: '',
          method: '',
          authType: '',
          authToken: '',
          headers: '',
          body: '',
          format: 'json',
          executeOnSubmit: true,
          showResponse: false,
          continueOnError: false
        }
      };
    } else {
      // Es un atributo de usuario
      newField = {
        id: `field-${Date.now()}`,
        type: 'attribute',
        name: field.name,
        label: field.name,
        inputType: field.dataType, // Usar dataType como inputType para mantener el esquema
        dataType: field.dataType,
        attributeId: field.id, // ID del atributo original
        required: true, // Por defecto true
        requestFromUser: true,
        description: field.description,
        useAI: field.useAI,
        closelist: field.closelist || [], // Incluir opciones de lista cerrada si existen
        multiple: field.multiple || false, // Incluir configuración de múltiple para closelist
        options: field.closelist || [], // Para compatibilidad con FieldCard
        updatable: false // Por defecto no es actualizable
      };
    }

    // Si es un campo API, siempre va al final
    if (newField.inputType === 'api') {
      setFormFields(prev => [...prev, newField]);
    } else {
      // Para otros campos, insertarlos antes de cualquier campo API existente
      setFormFields(prev => {
        const apiFieldIndex = prev.findIndex(f => f.inputType === 'api');
        if (apiFieldIndex !== -1) {
          // Hay campos API, insertar antes del primer API
          const newFields = [...prev];
          newFields.splice(apiFieldIndex, 0, newField);
          return newFields;
        } else {
          // No hay campos API, agregar al final
          return [...prev, newField];
        }
      });
    }
  };

  const handleDeleteField = (fieldId: string) => {
    setFormFields(prev => prev.filter(field => field.id !== fieldId));
  };

  const handleMoveFieldUp = (fieldId: string) => {
    setFormFields(prev => {
      const currentIndex = prev.findIndex(field => field.id === fieldId);
      const currentField = prev[currentIndex];
      
      // No permitir mover campos API
      if (currentField?.inputType === 'api') return prev;
      
      if (currentIndex <= 0) return prev; // Ya está en la primera posición o no se encontró
      
      const newFields = [...prev];
      const [movedField] = newFields.splice(currentIndex, 1);
      newFields.splice(currentIndex - 1, 0, movedField);
      return newFields;
    });
  };

  const handleMoveFieldDown = (fieldId: string) => {
    setFormFields(prev => {
      const currentIndex = prev.findIndex(field => field.id === fieldId);
      const currentField = prev[currentIndex];
      
      // No permitir mover campos API
      if (currentField?.inputType === 'api') return prev;
      
      if (currentIndex < 0 || currentIndex >= prev.length - 1) return prev; // Ya está en la última posición o no se encontró
      
      // No permitir mover un campo regular después de un campo API
      const targetField = prev[currentIndex + 1];
      if (targetField?.inputType === 'api') return prev;
      
      const newFields = [...prev];
      const [movedField] = newFields.splice(currentIndex, 1);
      newFields.splice(currentIndex + 1, 0, movedField);
      return newFields;
    });
  };

  const handleFieldUpdate = (fieldId: string, updates: Partial<FormField>) => {
    setFormFields(prev => prev.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    ));
  };

  const handleFormCreated = () => {
    if (isEditMode) {
      // En modo edición, navegar de vuelta a la lista de formularios
      navigate(`/agent/${agentId}/forms`);
    } else {
      // En modo creación, navegar a la lista de formularios después de crear
      navigate(`/agent/${agentId}/forms`);
    }
  };

  const handleBackToForms = () => {
    navigate(`/agent/${agentId}/forms`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading state para modo edición */}
      {isEditMode && (formLoading || isLoading) && (
        <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando formulario...</p>
          </div>
        </div>
      )}

      {/* Error state para modo edición */}
      {isEditMode && formError && (
        <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
            <div className="text-red-600 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error al cargar formulario</h3>
            <p className="text-gray-600 mb-4">No se pudo cargar el formulario para edición.</p>
            <button
              onClick={() => window.history.back()}
              className="btn btn-outline px-4 py-2"
            >
              Volver
            </button>
          </div>
        </div>
      )}

      {/* Desktop Left Panel */}
      <div className="hidden lg:block">
        <LeftPanel 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          agentId={agentId}
          onFieldClick={handleFieldClick}
        />
      </div>

      {/* Mobile Floating Button */}
      <div className="lg:hidden fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsMobileModalOpen(true)}
          className="btn btn-primary btn-circle shadow-lg hover:shadow-xl transition-shadow"
          title={t('add_fields', 'Agregar Campos')}
        >
          <PlusIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Modal */}
      <MobileFieldsModal
        isOpen={isMobileModalOpen}
        onClose={() => setIsMobileModalOpen(false)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        agentId={agentId}
        onFieldClick={handleFieldClick}
      />

      {/* Main Content Area */}
      <div className="lg:ml-80 lg:pl-20">
        <WorkspaceArea 
          formFields={formFields}
          formName={formName}
          formDescription={formDescription}
          onFormNameChange={setFormName}
          onFormDescriptionChange={setFormDescription}
          onDeleteField={handleDeleteField}
          onMoveFieldUp={handleMoveFieldUp}
          onMoveFieldDown={handleMoveFieldDown}
          onFieldUpdate={handleFieldUpdate}
          agentId={agentId}
          onFormCreated={handleFormCreated}
          aiCompleted={aiCompleted}
          onAiCompletedChange={setAiCompleted}
          isEditMode={isEditMode}
          formId={formId}
          onBackToForms={handleBackToForms}
        />
      </div>
    </div>
  );
};

export default FormCreator;
